import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner, Input } from '../../components/design-system';
import type { HostSpecialty } from '../../types';

type Editing = { id: string; name: string };

export function AdminHostSpecialties() {
  const [items, setItems] = useState<HostSpecialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data } = await supabase.from('host_specialties').select('*').order('sort_order');
    setItems((data ?? []) as HostSpecialty[]);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function handleAdd() {
    if (!newName.trim()) return;
    setSaving(true);
    const slug = newName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const maxOrder = items.reduce((m, c) => Math.max(m, c.sort_order), -1);
    await supabase.from('host_specialties').insert({ name: newName.trim(), slug, sort_order: maxOrder + 1 });
    setNewName('');
    setSaving(false);
    await load();
  }

  async function handleSaveEdit() {
    if (!editing) return;
    setSaving(true);
    await supabase.from('host_specialties').update({ name: editing.name }).eq('id', editing.id);
    setEditing(null);
    setSaving(false);
    await load();
  }

  async function handleReorder(id: string, direction: -1 | 1) {
    const idx = items.findIndex((c) => c.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const a = items[idx];
    const b = items[swapIdx];
    await Promise.all([
      supabase.from('host_specialties').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('host_specialties').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    await load();
  }

  async function toggleActive(item: HostSpecialty) {
    setSaving(true);
    await supabase.from('host_specialties').update({ active: !item.active }).eq('id', item.id);
    setSaving(false);
    await load();
  }

  const active = items.filter((c) => c.active);
  const inactive = items.filter((c) => !c.active);
  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Host Specialties</h1>
        <p className="text-app-muted text-sm mb-8">
          The fixed list hosts choose from on their profile. Keeps wording consistent for travelers and for Alma's matching.
        </p>

        {/* Add new */}
        <div className="flex gap-3 mb-8">
          <div className="flex-1">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New specialty name"
              onKeyDown={(e) => { if (e.key === 'Enter') void handleAdd(); }}
            />
          </div>
          <Button onClick={() => void handleAdd()} disabled={saving || !newName.trim()}>Add</Button>
        </div>

        {/* Active */}
        <div className="flex flex-col gap-2">
          {active.map((item, idx) => (
            <div key={item.id} className="bg-app-card border border-app-border rounded-xl px-4 py-3 flex items-center gap-3">
              {editing?.id === item.id ? (
                <>
                  <input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="flex-1 bg-app-bg border border-app-border rounded-lg px-3 py-1.5 text-sm text-app-fg focus:outline-none"
                  />
                  <Button size="sm" onClick={() => void handleSaveEdit()} disabled={saving}>Save</Button>
                  <button onClick={() => setEditing(null)} className="text-xs text-app-muted hover:text-app-fg">Cancel</button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium text-app-fg">{item.name}</span>
                    <span className="text-xs text-app-muted">({item.slug})</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => void handleReorder(item.id, -1)} disabled={idx === 0} className="p-1 text-app-muted hover:text-app-fg disabled:opacity-30 transition-colors" title="Move up">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button onClick={() => void handleReorder(item.id, 1)} disabled={idx === active.length - 1} className="p-1 text-app-muted hover:text-app-fg disabled:opacity-30 transition-colors" title="Move down">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button onClick={() => setEditing({ id: item.id, name: item.name })} className="text-xs text-app-muted hover:text-app-fg ml-2">Edit</button>
                    <button onClick={() => void toggleActive(item)} className="text-xs text-crimson/60 hover:text-crimson ml-2">Deactivate</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Inactive */}
        {inactive.length > 0 && (
          <div className="mt-8">
            <p className="text-xs uppercase tracking-widest text-app-muted font-medium mb-3">Deactivated</p>
            <div className="flex flex-col gap-2">
              {inactive.map((item) => (
                <div key={item.id} className="bg-app-card/50 border border-app-border rounded-xl px-4 py-3 flex items-center gap-3 opacity-60">
                  <span className="text-sm text-app-muted flex-1">{item.name} ({item.slug})</span>
                  <button onClick={() => void toggleActive(item)} className="text-xs text-success hover:text-success/80">Reactivate</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminHostSpecialties;
